import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Lenis from "lenis";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";
import axios from "axios";
import { MapPin, Phone, Mail, Check, ArrowUpRight } from "lucide-react";
import Navbar, { Logo } from "@/components/Navbar";
import Hero from "@/components/Hero";
import Chatbot from "@/components/Chatbot";
import WelcomeModal from "@/components/WelcomeModal";
import Gallery from "@/components/Gallery";
import PlotAvailability from "@/components/PlotAvailability";
import { CONTACT, IMAGES, INFRASTRUCTURE, AMENITIES, PLOTS, LEGAL, LANDMARKS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const Chapter = ({ n }) => (
  <span className="font-serif-display text-sm italic text-[var(--gold)]">{n}</span>
);

export default function Landing() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="relative bg-[var(--cream)]">
      <Navbar />
      <Hero />

      {/* Marquee ribbon */}
      <div className="border-y border-[var(--cream-3)] bg-[var(--cream-2)] py-5">
        <Marquee speed={38} gradient={false} autoFill>
          {["BMRDA Approved", "A-Katha", "Clear Title", "Ready for Registration", "Bank Loan Facility", "Gated Community"].map((t, i) => (
            <span key={i} className="mx-12 font-serif-display text-2xl italic text-[var(--ink-soft)] md:text-3xl">
              {t} <span className="ml-12 text-[var(--gold)]">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* Welcome / Manifesto */}
      <section id="welcome" className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[var(--gold)]">
                <Chapter n="01" /> — Welcome
              </p>
              <h2 className="font-serif-display text-4xl leading-[1.05] tracking-tight text-[var(--ink)] md:text-6xl">
                An exclusive gated community, <span className="italic text-[var(--gold-hover)]">crafted for modern living.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={0.15}>
              <p className="text-lg leading-relaxed text-[var(--ink-soft)]">
                Nova Royale is developed by <strong className="text-[var(--ink)]">N Murthy Groups</strong> — designed for
                modern living and smart investment. Spread across <strong className="text-[var(--ink)]">9 Acres 24 Guntas</strong> of
                thoughtfully planned land, the project offers spacious plots with excellent infrastructure and peaceful surroundings.
              </p>
              <p className="mt-6 text-lg leading-relaxed text-[var(--ink-soft)]">
                We purchase plain land and transform it into fully developed, ready-to-register plots. You own the land — you build your dream.
              </p>
              <div className="gold-rule mt-10" />
              <div className="mt-6 grid grid-cols-2 gap-6 sm:grid-cols-3">
                {[["9.24", "Acres of Land"], ["4", "Plot Sizes"], ["24×7", "Security"]].map(([a, b]) => (
                  <div key={b}>
                    <p className="font-serif-display text-4xl text-[var(--ink)]">{a}</p>
                    <p className="mt-1 text-xs uppercase tracking-widest text-[var(--ink-soft)]">{b}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Infrastructure — bento */}
      <section id="infrastructure" className="bg-[var(--ink)] py-24 md:py-36">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[var(--gold-bright)]">
              <Chapter n="02" /> — Services & Infrastructure
            </p>
            <h2 className="max-w-2xl font-serif-display text-4xl leading-tight tracking-tight text-[var(--cream)] md:text-5xl">
              Built underground, so life stays beautiful above.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-3">
            <Reveal className="md:col-span-1 md:row-span-2">
              <div className="group h-full overflow-hidden rounded-sm border border-[var(--gold)]/20">
                <img src={IMAGES.entrance} alt="Gated entrance and concrete roads" className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105 md:h-[420px]" />
                <div className="p-8">
                  <h3 className="font-serif-display text-2xl text-[var(--cream)]">Concrete Roads</h3>
                  <p className="mt-2 text-sm text-[var(--cream-3)]">60ft main road & 30ft sub roads with a secured brick compound wall.</p>
                </div>
              </div>
            </Reveal>
            {INFRASTRUCTURE.slice(2).map((item, i) => (
              <Reveal key={item.title} delay={i * 0.05}>
                <div className="h-full rounded-sm border border-[var(--gold)]/15 p-7 transition-colors duration-300 hover:border-[var(--gold)]/50 hover:bg-white/[0.03]">
                  <p className="mb-3 font-serif-display text-2xl italic text-[var(--gold)]">0{i + 1}</p>
                  <h3 className="text-base font-500 text-[var(--cream)]">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--cream-3)]">{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities */}
      <section id="amenities" className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[var(--gold)]">
              <Chapter n="03" /> — Amenities
            </p>
            <h2 className="font-serif-display text-4xl leading-tight tracking-tight text-[var(--ink)] md:text-5xl">
              A lake to unwind by. Courts to play on. A community to belong to.
            </h2>
            <ul className="mt-10 grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
              {AMENITIES.map((a, i) => (
                <li key={a} className="flex items-center gap-3 border-b border-[var(--cream-3)] py-3.5 text-[var(--ink-soft)]">
                  <span className="font-serif-display text-xs text-[var(--gold)]">0{i + 1}</span>
                  <span className="text-base">{a}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMAGES.lake} alt="Community lake" className="h-56 w-full rounded-sm border border-[var(--cream-3)] object-cover md:h-80" />
              <img src={IMAGES.park} alt="Landscaped park with fountain" className="mt-8 h-56 w-full rounded-sm border border-[var(--cream-3)] object-cover md:h-80" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Plots */}
      <section id="plots" className="bg-[var(--cream-2)] py-24 md:py-36">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[var(--gold)]">
              <Chapter n="04" /> — Plot Dimensions
            </p>
            <h2 className="max-w-2xl font-serif-display text-4xl leading-tight tracking-tight text-[var(--ink)] md:text-5xl">
              Choose the plot that fits your future.
            </h2>
          </Reveal>
          <div className="mt-14 divide-y divide-[var(--cream-3)] border-y border-[var(--cream-3)]">
            {PLOTS.map((p, i) => (
              <Reveal key={p.size} delay={i * 0.06}>
                <div className="group flex items-center justify-between py-8 transition-colors hover:bg-white/40">
                  <div className="flex items-baseline gap-6">
                    <span className="font-serif-display text-sm italic text-[var(--gold)]">0{i + 1}</span>
                    <span className="font-serif-display text-4xl tracking-tight text-[var(--ink)] md:text-6xl">{p.size}</span>
                  </div>
                  <div className="flex items-center gap-6">
                    <span className="hidden text-sm text-[var(--ink-soft)] sm:block">{p.note}</span>
                    <button
                      onClick={() => document.querySelector("#enquiry")?.scrollIntoView({ behavior: "smooth" })}
                      className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--gold)] text-[var(--gold-hover)] transition-all duration-300 group-hover:bg-[var(--gold)] group-hover:text-white"
                      data-testid={`plot-enquire-${i}`}
                    >
                      <ArrowUpRight size={18} />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-14 flex flex-wrap gap-4">
              {LEGAL.map((l) => (
                <span key={l} className="flex items-center gap-2 rounded-sm border border-[var(--cream-3)] bg-[var(--cream)] px-4 py-2.5 text-sm text-[var(--ink-soft)]">
                  <Check size={15} className="text-[var(--gold)]" /> {l}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <PlotAvailability />

      <Gallery />

      {/* Location */}
      <section id="location" className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-36">
        <div className="grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[var(--gold)]">
              <Chapter n="07" /> — Location
            </p>
            <h2 className="font-serif-display text-4xl leading-tight tracking-tight text-[var(--ink)] md:text-5xl">
              Perfectly placed in <span className="italic text-[var(--gold-hover)]">Bengaluru South.</span>
            </h2>
            <p className="mt-6 flex items-start gap-3 text-[var(--ink-soft)]">
              <MapPin size={20} className="mt-1 shrink-0 text-[var(--gold)]" />
              {CONTACT.address}
            </p>
            <img src={IMAGES.aerial} alt="Aerial view of the residential layout" className="mt-8 h-64 w-full rounded-sm border border-[var(--cream-3)] object-cover" />
            <div className="mt-4 overflow-hidden rounded-sm border border-[var(--cream-3)]">
              <iframe
                title="Nova Royale Location Map"
                src="https://www.google.com/maps?q=12.929977,77.3955974&z=16&output=embed"
                className="h-56 w-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="location-map"
              />
            </div>
            <a
              href="https://maps.app.goo.gl/FsqcEm8guMaMGNV49"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--gold-hover)] link-underline"
              data-testid="location-directions"
            >
              <MapPin size={15} /> Get Directions to Nova Royale
            </a>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <ul className="divide-y divide-[var(--cream-3)] border-t border-[var(--cream-3)]">
              {LANDMARKS.map((l, i) => (
                <Reveal key={l.place} delay={i * 0.05}>
                  <li className="flex items-center justify-between py-6">
                    <span className="max-w-[70%] text-lg text-[var(--ink)]">{l.place}</span>
                    <span className="font-serif-display text-3xl text-[var(--gold-hover)]">{l.dist}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <EnquirySection />
      <Footer />
      <Chatbot />
      <WelcomeModal />
    </div>
  );
}

function EnquirySection() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", plot_preference: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry`, form);
      toast.success("Thank you! Our team will reach out to you shortly.");
      setForm({ name: "", phone: "", email: "", plot_preference: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call +91 73491 39671.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full border-b border-white/25 bg-transparent py-3 text-lg text-[var(--cream)] outline-none transition-colors placeholder:text-[var(--cream-3)]/50 focus:border-[var(--gold)]";

  return (
    <section id="enquiry" className="relative overflow-hidden bg-[var(--ink)] py-24 md:py-36">
      <div className="absolute inset-0 opacity-[0.12]">
        <img src={IMAGES.field} alt="" className="h-full w-full object-cover" />
      </div>
      <div className="relative mx-auto grid max-w-7xl gap-16 px-5 md:px-10 lg:grid-cols-2">
        <Reveal>
          <p className="mb-6 text-xs uppercase tracking-[0.4em] text-[var(--gold-bright)]">
            <Chapter n="08" /> — Enquire
          </p>
          <h2 className="font-serif-display text-4xl leading-tight tracking-tight text-[var(--cream)] md:text-6xl">
            Schedule a site visit today.
          </h2>
          <p className="mt-6 max-w-md text-lg text-[var(--cream-3)]">
            Share your details and our team will get in touch with pricing, availability and a guided tour of Nova Royale.
          </p>
          <div className="mt-10 space-y-4">
            {CONTACT.phones.map((p) => (
              <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="flex items-center gap-3 text-[var(--cream)] transition-colors hover:text-[var(--gold-bright)]" data-testid={`contact-phone-${p.replace(/\D/g,'')}`}>
                <Phone size={18} className="text-[var(--gold)]" /> {p}
              </a>
            ))}
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-[var(--cream)] transition-colors hover:text-[var(--gold-bright)]" data-testid="contact-email">
              <Mail size={18} className="text-[var(--gold)]" /> {CONTACT.email}
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="space-y-7" data-testid="enquiry-form">
            <input className={field} placeholder="Full Name *" value={form.name} onChange={set("name")} data-testid="enquiry-name" />
            <input className={field} placeholder="Phone Number *" value={form.phone} onChange={set("phone")} data-testid="enquiry-phone" />
            <input className={field} type="email" placeholder="Email Address *" value={form.email} onChange={set("email")} data-testid="enquiry-email" />
            <select className={`${field} cursor-pointer`} value={form.plot_preference} onChange={set("plot_preference")} data-testid="enquiry-plot" style={{ colorScheme: "dark" }}>
              <option value="" className="bg-[var(--ink)] text-[var(--cream)]">Preferred Plot Size</option>
              {PLOTS.map((p) => <option key={p.size} value={p.size} className="bg-[var(--ink)] text-[var(--cream)]">{p.size}</option>)}
            </select>
            <textarea className={`${field} resize-none`} rows={3} placeholder="Your message (optional)" value={form.message} onChange={set("message")} data-testid="enquiry-message" />
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-sm bg-[var(--gold)] py-4 text-xs uppercase tracking-[0.25em] text-[#1C2427] transition-transform duration-300 hover:-translate-y-0.5 hover:bg-[var(--gold-bright)] disabled:opacity-60"
              data-testid="enquiry-submit"
            >
              {loading ? "Sending…" : "Submit Enquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {  return (
    <footer className="border-t border-[var(--cream-3)] bg-[var(--cream)] py-16" data-testid="footer">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <Logo />
          <p className="max-w-sm text-sm text-[var(--ink-soft)]">
            {CONTACT.address}
          </p>
        </div>
        <div className="gold-rule my-10" />
        <div className="flex flex-col items-start justify-between gap-4 text-xs text-[var(--ink-soft)] md:flex-row md:items-center">
          <p>© {new Date().getFullYear()} N Murthy Groups · Nova Royale. All rights reserved.</p>
          <p className="uppercase tracking-[0.3em]">Invest Today · Build Your Dream Tomorrow</p>
        </div>
      </div>
    </footer>
  );
}
